import { useState, useEffect, useRef } from 'react'
import LogRocket from 'logrocket'
import type { FC, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { experimentalStyled } from '@material-ui/core/styles'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from '@material-ui/core'
import { updateContact } from 'src/slices/contacts'
import { Contact } from 'src/types/contact'
import { getKazooVoicemails } from 'src/slices/voicemail'
import { formatNumber } from '../../utils/common'
import DashboardNavbar from './DashboardNavbar'
import DashboardSidebar from './DashboardSidebar'
import PhoneBar from './PhoneBar'
import store, { RootState } from '../../store'
import useAuth from '../../hooks/useAuth'
import {
  updateUsers,
  updateConferences,
  setSelectedCall,
  removeConference,
  updateKazooLiveNotes,
  removeKazooCallSession
} from '../../slices/phone'
import { insertSms, insertChat } from '../../slices/sms'
import { updateCommunication } from '../../slices/communications'
import IntroTour from '../tour/IntroTour'
import NoteBar from './NoteBar'

interface DashboardLayoutProps {
  children?: ReactNode
}

const firstVisit: boolean = !!!localStorage.getItem('firstVisit')
const DashboardLayoutRoot = experimentalStyled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  width: '100%'
}))

const DashboardLayoutWrapper = experimentalStyled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  paddingTop: '64px',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: '190px'
    // paddingLeft: '183px'
  }
}))

const DashboardLayoutContainer = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
})

const DashboardLayoutContent = experimentalStyled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch'
})

const websocketURL =
  process.env.REACT_APP_STAGE === 'prod'
    ? 'wss://64e3fp2r39.execute-api.ca-central-1.amazonaws.com/prod'
    : 'wss://1myoriazn2.execute-api.ca-central-1.amazonaws.com/alpha'

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const { user, account, statusMessages, tokens } = useAuth()
  const { auth0Token } = user
  const socket = useRef(null)
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState<boolean>(false)
  const selectedCall = useSelector(
    (state: RootState) => state.phone.selectedCall
  )
  const selectedNote = useSelector(
    (state: RootState) => state.phone.selectedNote
  )
  const liveNotes = useSelector((state: RootState) => state.phone.liveNotes)
  const kazooUsers = useSelector((state: RootState) => state.phone.users)

  const currentMessage = statusMessages[0]
  let firstOpen = false
  if (currentMessage) {
    const read = localStorage.getItem(currentMessage.id)
    if (!read) firstOpen = true
  }
  const [messageOpen, setMessageOpen] = useState(firstOpen)

  const setMessageRead = (id: string) => {
    setMessageOpen(false)
    localStorage.setItem(id, 'read')
  }
  const { smsOnly, kazooOnly } = account

  const phoneEnabled = typeof smsOnly === 'boolean' && smsOnly === false
  const kazooEnabled =
    typeof kazooOnly === 'boolean' && kazooOnly === true && phoneEnabled

  const handleConfBody = (body) => {
    const completedStates = ['completed', 'abandoned', 'missed']
    // Use store.getState since being passed as an event handler function saves selector variables at time of listener init.
    if (
      completedStates.includes(body.conference.conf_status) &&
      (!store.getState().phone.liveNotes[body.conference.conferenceSid] ||
        !store.getState().phone.liveNotes[body.conference.conferenceSid]?.saved)
    ) {
      if (
        !store.getState().phone.selectedCall === body.conference.conferenceSid
      ) {
        dispatch(setSelectedCall('new'))
      }
      dispatch(removeConference(body.conference.conferenceSid))
      return
    }
    if (body.event === 'startConference') {
      dispatch(updateConferences({ ...body.conference, incoming: true }))
    } else {
      dispatch(updateConferences(body.conference))
    }
  }

  const getVoicemails = () => {
    const kazooUser = kazooUsers[user.australisId?.replaceAll('-', '')]
    dispatch(
      getKazooVoicemails(
        user.auth0Token,
        kazooUsers.account,
        kazooUser.kazooId,
        tokens.kazooAuthToken
      )
    )
  }

  // Init websocket connection
  const initSocket = () => {
    if (socket.current) {
      socket.current.onopen = null
      socket.current.onmessage = null
      socket.current.onclose = null
    }
    socket.current = new WebSocket(websocketURL)
    socket.current.onopen = () => {
      console.log('connected websocket')
      if (socket.current.readyState === 1) {
        socket.current.send(JSON.stringify({ action: 'auth', tokens }))
      }
    }
    socket.current.onclose = (event) => {
      console.log('Socket has closed')
      console.log('Reconnecting... ')
      initSocket()
    }
    socket.current.onmessage = (event) => {
      const body = JSON.parse(event.data)

      console.log('body', body)
      if (body.channelEvent) {
        if (process.env.NODE_ENV !== 'development') {
          LogRocket.track('channelEvent', body)
        }
        const isChannelCreateOrDestroy = [
          'channel_create',
          'channel_destroy'
        ].includes(body.channelEvent)
        if (isChannelCreateOrDestroy) {
          dispatch(updateKazooLiveNotes(body))
        }
        if (body.channelEvent === 'channel_destroy') {
          // wait until user records message
          setTimeout(() => {
            getVoicemails()
          }, 40000)
          dispatch(removeKazooCallSession(auth0Token, body.interactionId, true))
        }
      }

      if (body.contact) {
        const contact = body.contact as Contact
        if (body.event === 'update') {
          dispatch(updateContact(contact))
        }
      }
      if (body.sms) {
        if (body.sms.toNumber && tokens[body.sms.toNumber]) {
          const number = formatNumber(body.sms.fromNumber)
          const { contactNumberMap } = store.getState().contacts
          const contact = contactNumberMap[number]

          const subject = contact
            ? `Message from ${contact.contactDetails?.firstName || ''} ${
                contact.contactDetails?.lastName || ''
              }`
            : `Message from ${number}`
          const textBody = body.sms.text || `...`

          const notif = new Notification(subject, {
            body: textBody,
            tag: `new-message:${body.sms.id}`,
            renotify: true
          })
        }
        dispatch(insertSms(body.sms))
      }
      if (body.chat) {
        dispatch(insertChat(body.chat))
      }
      if (
        body.conference &&
        body.conference.conferenceSid !== 'init' &&
        body.event !== 'startConference'
      ) {
        handleConfBody(body)
      }
      if (body.voipUser) {
        dispatch(updateUsers(body.voipUser))
      }
      if (body.communication) {
        dispatch(updateCommunication(body.communication))
      }
    }
  }

  const setupNotifications = () => {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        enqueueSnackbar('Browser notifications enabled!', {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom'
          },
          variant: 'success'
        })
      } else {
        enqueueSnackbar(
          'Please consider enabling browser notifications to be properly notified regarding incoming messages/calls',
          {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom'
            },
            variant: 'error'
          }
        )
      }
    })
  }

  function offlineListener() {
    console.log('offline')
  }
  function onlineListener() {
    console.log('online')
    if (
      window.confirm(
        `A network change has been detected.\nConfirm to reload, or cancel to finish current tasks and refresh manually with Ctrl/Cmd + R.`
      )
    ) {
      window.location.reload()
    }
  }

  useEffect(() => {
    if (Notification.permission !== 'granted') {
      setupNotifications()
    }

    window.addEventListener('offline', offlineListener)
    window.addEventListener('online', onlineListener)
    return () => {
      window.removeEventListener('offline', offlineListener)
      window.removeEventListener('online', onlineListener)
    }
  }, [])

  useEffect(() => {
    if (user) {
      // make sure kazoo is available if at all
      setTimeout(() => {
        initSocket()
      }, 3000)
    }
  }, [kazooUsers])

  //   console.log('selected note in dashboard', selectedNote)
  //   console.log('live notes in dashboard', liveNotes)

  return (
    <DashboardLayoutRoot>
      <IntroTour />
      <DashboardNavbar
        onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)}
      />
      <DashboardSidebar
        onMobileClose={(): void => setIsSidebarMobileOpen(false)}
        openMobile={isSidebarMobileOpen}
        phoneEnabled={phoneEnabled}
      />

      {phoneEnabled && !kazooEnabled ? <PhoneBar /> : null}
      {kazooEnabled ? <NoteBar /> : null}

      <DashboardLayoutWrapper>
        <DashboardLayoutContainer>
          <DashboardLayoutContent>
            <Outlet />
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
      <div
        style={{
          minWidth: !phoneEnabled ? 0 : !!selectedCall ? '360px' : '80px',
          display: kazooEnabled ? 'none' : 'block'
        }}
      />
      <div
        style={{
          minWidth: !Object.keys(liveNotes).length
            ? 0
            : selectedNote && Object.keys(liveNotes).length > 1
            ? '480px'
            : selectedNote && Object.keys(liveNotes).length === 1
            ? '380px'
            : '110px',
          display: kazooEnabled ? 'block' : 'none'
        }}
      />
      {statusMessages.length > 0 ? (
        <Dialog
          open={messageOpen}
          onClose={() => setMessageRead(currentMessage.id)}
        >
          <DialogTitle>{statusMessages[0].title}</DialogTitle>
          <DialogContent sx={{ width: 500 }}>
            <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
              {statusMessages[0].body}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setMessageRead(currentMessage.id)}
              color="primary"
            >
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </DashboardLayoutRoot>
  )
}

export default DashboardLayout
